type JsonValue = any;

import { getAuthToken } from "./authToken";

const getXsrfCookie = (): string => {
  const matches = document.cookie.match(/\bXSRF-TOKEN=([^\s;]+)/);
  try {
    return matches && matches[1] ? decodeURIComponent(matches[1]) : "";
  } catch {
    return "";
  }
};

const getApiBaseUrl = (): string => {
  const w = window as any;
  const apiUrl = w && w.Penmate && w.Penmate.env && w.Penmate.env.apiUrl;
  if (!apiUrl || typeof apiUrl !== "string") {
    throw new Error("Penmate.env.apiUrl is not available");
  }
  return apiUrl.replace(/\/+$/, "");
};

export type PmFetchOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  params?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  headers?: Record<string, string>;
};

export type PmFetchJsonResult<T> = {
  ok: boolean;
  status: number;
  data: T | null;
  text: string;
  url: string;
};

export async function pmFetchJsonResult<T = JsonValue>(
  path: string,
  { method = "GET", params, body, headers }: PmFetchOptions = {},
): Promise<PmFetchJsonResult<T>> {
  const baseUrl = getApiBaseUrl();
  const url = new URL(`${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }

  const reqHeaders: Record<string, string> = {
    ...headers,
  };

  const token = await getAuthToken();
  if (token) reqHeaders["Authorization"] = `Bearer ${token}`;

  const isGet = method === "GET";
  if (!isGet) {
    reqHeaders["Content-Type"] = reqHeaders["Content-Type"] || "application/json; charset=UTF-8";
    const xsrf = getXsrfCookie();
    if (xsrf) reqHeaders["X-XSRF-TOKEN"] = xsrf;
  }

  const res = await fetch(url.toString(), {
    method,
    credentials: "include",
    headers: reqHeaders,
    body: isGet ? undefined : JSON.stringify(body ?? {}),
  });

  const text = await res.text().catch(() => "");
  const contentType = res.headers.get("content-type") || "";
  const looksJson = contentType.includes("application/json") || text.trim().startsWith("{") || text.trim().startsWith("[");

  let data: T | null = null;
  if (looksJson && text.trim()) {
    try {
      data = JSON.parse(text) as T;
    } catch {
      data = null;
    }
  }

  return {
    ok: res.ok,
    status: res.status,
    data,
    text,
    url: url.toString(),
  };
}

export async function pmFetchJson<T = JsonValue>(
  path: string,
  { method = "GET", params, body, headers }: PmFetchOptions = {},
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const url = new URL(`${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }

  const reqHeaders: Record<string, string> = {
    ...headers,
  };

  const token = await getAuthToken();
  if (token) reqHeaders["Authorization"] = `Bearer ${token}`;

  const isGet = method === "GET";
  if (!isGet) {
    reqHeaders["Content-Type"] = reqHeaders["Content-Type"] || "application/json; charset=UTF-8";
    const xsrf = getXsrfCookie();
    if (xsrf) reqHeaders["X-XSRF-TOKEN"] = xsrf;
  }

  const res = await fetch(url.toString(), {
    method,
    credentials: "include",
    headers: reqHeaders,
    body: isGet ? undefined : JSON.stringify(body ?? {}),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${method} ${url.pathname} failed (${res.status}): ${text || res.statusText}`);
  }

  return (await res.json()) as T;
}
